import cookie from 'vue-cookies';
import { USER_TYPE } from '@/utils/const';
import { isMotorRenewal } from '@/utils/agentRenewal';
import {
  fetchAllAgentRenewals,
  fetchAllClientRenewals,
  fetchRenewalsByIdNumber,
  fetchRenewalsByMobile,
} from '@/services/renewalApi';
import {
  groupReminders,
  reminderSummary,
  sortReminders,
  toReminder,
} from '@/utils/renewalReminders';

export const REMINDER_REFRESH_MS = 5 * 60 * 1000;
const MOTOR_COVT = '070';

let inflight = null;

function currentAudience() {
  return cookie.get(USER_TYPE) === 'agent' ? 'agent' : 'customer';
}

function ownerKeyOf(user, audience) {
  if (!user) return '';
  return `${audience}:${user.sub || user.email || user['custom:id_number'] || user.phone_number || ''}`;
}

function customerList(result) {
  if (!result?.success) return [];
  const raw = result.data?.pageItems || result.data?.renewals || result.data;
  return Array.isArray(raw) ? raw : [];
}

function uniqueClientCodes(clients) {
  const codes = (Array.isArray(clients) ? clients : [])
    .map((c) => c?.clientCode)
    .filter((code) => code != null && code !== '');
  return [...new Set(codes.map(String))];
}

/** Upcoming and lapsed renewals for every client record the customer holds; null when GIS cannot answer. */
async function loadClientCodeRenewals(clientCodes) {
  const results = await Promise.all(clientCodes.flatMap((clientCode) => ['pending', 'lapsed']
    .map((type) => fetchAllClientRenewals({ clientCode, type }))));
  if (!results.some((r) => r.success)) return null;
  const seen = new Set();
  return results.flatMap((r) => r.data || []).filter((r) => {
    const key = `${r.policyNo}-${r.effectiveBatchNo || r.batchNo}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

async function loadLegacyCustomerRenewals(user) {
  const idNumber = user?.['custom:id_number'];
  const phone = user?.phone_number || user?.['custom:phone_number'];
  if (idNumber) return customerList(await fetchRenewalsByIdNumber(idNumber, MOTOR_COVT));
  if (phone) return customerList(await fetchRenewalsByMobile(phone, MOTOR_COVT));
  return [];
}

async function loadCustomerRenewals(user, { dispatch, rootState }) {
  try {
    await dispatch('portalCache/fetchCustomerClient', {}, { root: true });
  } catch {
    /* without a client code we fall back to the ID / phone lookups */
  }
  const clientCodes = uniqueClientCodes(rootState.client);
  if (clientCodes.length) {
    const list = await loadClientCodeRenewals(clientCodes);
    if (list) return list;
  }
  return loadLegacyCustomerRenewals(user);
}

async function loadAgentRenewals(user) {
  const res = await fetchAllAgentRenewals({
    agnCode: user?.['custom:agent_code'],
    email: user?.email,
    type: 'pending',
  });
  return res.success ? res.data : [];
}

export default {
  namespaced: true,
  state: {
    list: [],
    ownerKey: '',
    loading: false,
    loaded: false,
    fetchedAt: 0,
  },
  getters: {
    reminders: (state) => sortReminders(state.list.map((r) => toReminder(r))),
    /** Policies that can still be renewed; drives the badge and the reminder panel. */
    actionable: (state, getters) => getters.reminders.filter((r) => r.renewable),
    groups: (state, getters) => groupReminders(getters.reminders),
    summary: (state, getters) => reminderSummary(getters.actionable),
    count: (state, getters) => getters.actionable.length,
  },
  mutations: {
    setLoading(state, value) {
      state.loading = value;
    },
    setList(state, { list, ownerKey }) {
      state.list = list;
      state.ownerKey = ownerKey;
      state.loaded = true;
      state.fetchedAt = Date.now();
    },
    reset(state) {
      state.list = [];
      state.ownerKey = '';
      state.loaded = false;
      state.fetchedAt = 0;
    },
  },
  actions: {
    async refresh({
      state, commit, dispatch, rootGetters, rootState,
    }, { force = false } = {}) {
      const user = rootGetters['auth/authenticatedUser'];
      const audience = currentAudience();
      const ownerKey = ownerKeyOf(user, audience);
      if (!user || !ownerKey) return;
      if (state.ownerKey && state.ownerKey !== ownerKey) commit('reset');
      const fresh = state.loaded && Date.now() - state.fetchedAt < REMINDER_REFRESH_MS;
      if (!force && fresh) return;
      if (inflight) {
        await inflight;
        return;
      }
      commit('setLoading', true);
      inflight = (audience === 'agent'
        ? loadAgentRenewals(user)
        : loadCustomerRenewals(user, { dispatch, rootState }))
        .then((list) => commit('setList', { list: list.filter(isMotorRenewal), ownerKey }))
        .catch(() => {})
        .finally(() => {
          inflight = null;
          commit('setLoading', false);
        });
      await inflight;
    },
    /** Pages that already loaded the same list share it, so the badge never disagrees with the page. */
    sync({ commit, rootGetters }, list) {
      const user = rootGetters['auth/authenticatedUser'];
      const ownerKey = ownerKeyOf(user, currentAudience());
      if (!ownerKey || !Array.isArray(list)) return;
      commit('setList', { list: list.filter(isMotorRenewal).map((r) => ({ ...r })), ownerKey });
    },
  },
};
